import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import mermaid from 'mermaid';
import { BookOpen, Loader2, RefreshCw, Save, Trash2 } from 'lucide-react';
import { organisationsAPI, projectsAPI } from '../../services/api';
import toast from '../../services/toast';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import ProjectDocumentsPanel from './ProjectDocumentsPanel';
import PortfolioTeamPanel from './PortfolioTeamPanel';
import {
  AnimatedTabIndicator,
  animatedTabTriggerClass,
} from '../../components/ui/AnimatedTabIndicator';
import {
  formatCodeIndexStageLabel,
  formatCodeIndexStatusLabel,
  isCodeIndexInProgress,
  codeIndexStatusBadgeClassForRepo,
} from '../../utils/codeIndexProgress';

const AUTHOR_ROLES = ['PM', 'ANALYST', 'ENGINEERING', 'QA'];

const PORTFOLIO_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'tech', label: 'Tech & Architecture' },
  { id: 'team', label: 'Team' },
  { id: 'settings', label: 'Settings' },
];

function slugifyHeading(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 80);
}

function extractH3Headings(markdown) {
  const text = String(markdown || '');
  const out: string[] = [];
  for (const line of text.split('\n')) {
    const m = line.match(/^###\s+(.+)\s*$/);
    if (m && m[1]) out.push(m[1].trim());
  }
  return out;
}

function isLikelyCitationInlineCode(text) {
  const t = String(text || '').trim();
  return /^\[(repo|knowledge):/.test(t);
}

function parseRepoCitationUrl(text) {
  const m = String(text || '')
    .trim()
    .match(/^\[repo:([^\]]+)\]\s+(.+?)(?:#\d+)?$/);
  if (!m) return null;
  const repoPath = m[1].trim();
  const filePath = m[2].trim();
  return `https://github.com/${repoPath}/blob/main/${filePath}`;
}

function formatBriefingDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function buildStaleBriefingTitle(readiness) {
  if (readiness?.wikiStale !== true) return null;
  const genRaw = readiness.wikiGeneratedAt;
  const gen = genRaw ? new Date(genRaw) : null;
  const codeAt = readiness.latestCodeIndexAt ? new Date(readiness.latestCodeIndexAt) : null;
  const knowAt = readiness.latestKnowledgeSourceAt
    ? new Date(readiness.latestKnowledgeSourceAt)
    : null;
  const codeStale =
    gen &&
    codeAt &&
    !Number.isNaN(gen.getTime()) &&
    !Number.isNaN(codeAt.getTime()) &&
    gen.getTime() < codeAt.getTime();
  const knowStale =
    gen &&
    knowAt &&
    !Number.isNaN(gen.getTime()) &&
    !Number.isNaN(knowAt.getTime()) &&
    gen.getTime() < knowAt.getTime();
  const genStr = formatBriefingDate(genRaw);
  const codeStr = formatBriefingDate(readiness.latestCodeIndexAt);
  if (codeStale && knowStale) {
    return 'Code and knowledge sources have been updated since this briefing was generated.';
  }
  if (codeStale && codeStr && genStr) {
    return `Code was re-indexed on ${codeStr} — briefing was generated on ${genStr}.`;
  }
  if (knowStale) {
    return 'A knowledge source was updated after this briefing was generated.';
  }
  return 'Regenerate for fresher retrieval.';
}

const PORTFOLIO_MARKDOWN_SANITIZE_SCHEMA = {
  ...defaultSchema,
  tagNames: Array.from(
    new Set([
      ...(defaultSchema.tagNames || []),
      'details',
      'summary',
      'kbd',
      'br',
      'u',
      'mark',
      'del',
      'ins',
      'sup',
      'sub',
    ])
  ),
  attributes: {
    ...(defaultSchema.attributes || {}),
    a: Array.from(
      new Set([...(defaultSchema.attributes?.a || []), 'href', 'title', 'target', 'rel'])
    ),
    h2: Array.from(new Set([...(defaultSchema.attributes?.h2 || []), 'id'])),
    h3: Array.from(new Set([...(defaultSchema.attributes?.h3 || []), 'id'])),
    td: Array.from(new Set([...(defaultSchema.attributes?.td || []), 'align'])),
    th: Array.from(new Set([...(defaultSchema.attributes?.th || []), 'align'])),
  },
  protocols: {
    ...(defaultSchema.protocols || {}),
    href: ['http', 'https', 'mailto'],
  },
};

let mermaidInitialized = false;
function ensureMermaidInit() {
  if (mermaidInitialized) return;
  mermaidInitialized = true;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    themeVariables: {
      background: '#0f1117',
      primaryColor: '#1e2230',
      primaryTextColor: '#e2e8f0',
      primaryBorderColor: '#2d3748',
      lineColor: '#4a5568',
      secondaryColor: '#1a202c',
      tertiaryColor: '#2d3748',
      edgeLabelBackground: '#1a202c',
      clusterBkg: '#1a202c',
    },
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 13,
  });
}

let mermaidCounter = 0;
function MermaidDiagram({ code }: any) {
  const ref = React.useRef<any>(null);
  const idRef = React.useRef(`mermaid-diagram-${++mermaidCounter}`);

  React.useEffect(() => {
    if (!ref.current || !code) return;
    ensureMermaidInit();
    let cancelled = false;
    mermaid
      .render(idRef.current, code.trim())
      .then(({ svg }) => {
        if (!cancelled && ref.current) {
          ref.current.innerHTML = svg;
        }
      })
      .catch(() => {
        if (!cancelled && ref.current) {
          ref.current.innerHTML = `<pre class="text-xs text-slate-400 whitespace-pre-wrap">${code}</pre>`;
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  return (
    <div
      ref={ref}
      className="my-4 flex justify-center overflow-x-auto rounded-lg border border-dark-border bg-dark-base/60 p-4 [&_svg]:max-w-full"
    />
  );
}

/**
 * @param {{
 *   orgId: string,
 *   streamId: string,
 *   projectId: string,
 *   projectName: string,
 *   repositories: Array<Record<string, unknown>>,
 * }} props
 */
export default function ProjectPortfolioWikiTab({
  orgId,
  streamId,
  projectId,
  projectName,
  repositories,
}: any) {
  const wikiRead = useFeatureFlag('api.projects.portfolioWiki.read', false);
  const wikiWrite = useFeatureFlag('api.projects.portfolioWiki.generate', false);
  const wikiDebugRead = useFeatureFlag('api.projects.portfolioWiki.debug.read', false);
  const knowRead = useFeatureFlag('api.projects.knowledgeSources.read', false);
  const knowWrite = useFeatureFlag('api.projects.knowledgeSources.write', false);

  const [loading, setLoading] = useState(true);
  const [wiki, setWiki] = useState<any>(null);
  const [readiness, setReadiness] = useState<any>({});
  const [assignments, setAssignments] = useState<any[]>([]);
  const [orgMembers, setOrgMembers] = useState<any[]>([]);
  const [editorial, setEditorial] = useState('');
  const [savingEditorial, setSavingEditorial] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [savingAuthors, setSavingAuthors] = useState(false);
  const [retrievalDebugOpen, setRetrievalDebugOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const onTabKeyDown = useCallback(
    (e) => {
      const ids = PORTFOLIO_TABS.map((t) => t.id);
      const idx = ids.indexOf(activeTab);
      if (idx < 0) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveTab(ids[(idx + 1) % ids.length]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveTab(ids[(idx - 1 + ids.length) % ids.length]);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveTab(ids[0]);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveTab(ids[ids.length - 1]);
      }
    },
    [activeTab]
  );

  const loadWiki = useCallback(async () => {
    if (!wikiRead) return;
    const data = await projectsAPI.getPortfolioWiki(orgId, streamId, projectId, {
      includeRetrievalDebug: wikiDebugRead,
    });
    setWiki(data.wiki ?? null);
    setReadiness(data.readiness ?? {});
    setEditorial(data.wiki?.editorialInstructions ?? '');
  }, [wikiRead, wikiDebugRead, orgId, streamId, projectId]);

  const loadAssignments = useCallback(async () => {
    if (!wikiRead) return;
    const list = await projectsAPI.listPortfolioAuthorAssignments(orgId, streamId, projectId);
    setAssignments(Array.isArray(list) ? list : []);
  }, [wikiRead, orgId, streamId, projectId]);

  const loadOrgMembers = useCallback(async () => {
    if (!orgId) return;
    try {
      const list = await organisationsAPI.getMembers(orgId, { projectId });
      setOrgMembers(Array.isArray(list) ? list : []);
    } catch {
      setOrgMembers([]);
    }
  }, [orgId, projectId]);

  const refreshAll = useCallback(async () => {
    if (!wikiRead && !knowRead) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      await Promise.all([loadWiki(), loadAssignments(), loadOrgMembers()]);
    } catch (e) {
      toast.error((e as Error).message || 'Failed to load portfolio wiki');
    } finally {
      setLoading(false);
    }
  }, [wikiRead, knowRead, loadWiki, loadAssignments, loadOrgMembers]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const genBusy = wiki?.generationStatus === 'queued' || wiki?.generationStatus === 'running';

  const techHeadings = useMemo(
    () => extractH3Headings(wiki?.techFeaturesMarkdown),
    [wiki?.techFeaturesMarkdown]
  );
  const showTechToc = techHeadings.length >= 2;

  const markdownComponents = useMemo(
    () => ({
      a: ({ href, children, ...props }) => {
        const url = typeof href === 'string' ? href : '';
        const isExternal = /^https?:\/\//i.test(url) || /^mailto:/i.test(url);
        if (isExternal) {
          return (
            <a {...props} href={url} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          );
        }
        return (
          <a {...props} href={url}>
            {children}
          </a>
        );
      },
      h2: ({ children, ...props }) => {
        const text = Array.isArray(children) ? children.join('') : String(children || '');
        const id = slugifyHeading(text);
        return (
          <h2 id={id || undefined} {...props}>
            {children}
          </h2>
        );
      },
      h3: ({ children, ...props }) => {
        const text = Array.isArray(children) ? children.join('') : String(children || '');
        const id = slugifyHeading(text);
        return (
          <h3 id={`tech-${id}`} {...props}>
            {children}
          </h3>
        );
      },
      code: ({ inline, className, children, ...props }) => {
        const text = Array.isArray(children) ? children.join('') : String(children || '');
        const lang = /language-(\w+)/.exec(className || '')?.[1];
        if (lang === 'mermaid') {
          return <MermaidDiagram code={text} />;
        }
        if (inline && isLikelyCitationInlineCode(text)) {
          const citationUrl = parseRepoCitationUrl(text);
          const codeEl = (
            <code
              {...props}
              className="rounded bg-dark-elevated px-1 py-0.5 text-[11px] text-slate-300/60"
            >
              {children}
            </code>
          );
          if (citationUrl) {
            return (
              <a
                href={citationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline no-underline"
              >
                {codeEl}
              </a>
            );
          }
          return codeEl;
        }
        return <code {...props}>{children}</code>;
      },
      table: ({ children, ...props }) => {
        return (
          <div className="overflow-x-auto">
            <table {...props}>{children}</table>
          </div>
        );
      },
    }),
    []
  );

  useEffect(() => {
    if (!genBusy) return;
    const t = setInterval(() => {
      if (wikiRead) loadWiki().catch(() => undefined); // background poll
    }, 4000);
    return () => clearInterval(t);
  }, [genBusy, wikiRead, loadWiki]);

  const handleSaveEditorial = async () => {
    if (!wikiWrite) return;
    setSavingEditorial(true);
    try {
      const w = await projectsAPI.patchPortfolioWiki(orgId, streamId, projectId, {
        editorialInstructions: editorial,
      });
      setWiki(w);
      toast.success('Editorial instructions saved');
    } catch (e) {
      toast.error((e as Error).message || 'Save failed');
    } finally {
      setSavingEditorial(false);
    }
  };

  const handleRegenerate = async () => {
    if (!wikiWrite) return;
    setRegenerating(true);
    try {
      const w = await projectsAPI.regeneratePortfolioWiki(orgId, streamId, projectId);
      setWiki(w);
      toast.success('Regeneration queued');
    } catch (e) {
      toast.error((e as Error).message || 'Could not queue regeneration');
    } finally {
      setRegenerating(false);
    }
  };

  const addAuthorRow = () => {
    setAssignments((prev) => [
      ...prev,
      { role: 'ENGINEERING', userId: null, displayName: '', sortOrder: prev.length },
    ]);
  };

  const updateAuthorRow = (index, patch) => {
    setAssignments((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeAuthorRow = (index) => {
    setAssignments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveAuthors = async () => {
    if (!wikiWrite) return;
    setSavingAuthors(true);
    try {
      const payload = assignments.map((a, i) => ({
        role: String(a.role || 'ENGINEERING').toUpperCase(),
        userId: a.userId && String(a.userId).trim() ? String(a.userId).trim() : null,
        displayName:
          a.displayName && String(a.displayName).trim() ? String(a.displayName).trim() : null,
        sortOrder: i,
      }));
      const next = await projectsAPI.putPortfolioAuthorAssignments(
        orgId,
        streamId,
        projectId,
        payload
      );
      setAssignments(Array.isArray(next) ? next : []);
      toast.success('Author assignments saved');
    } catch (e) {
      toast.error((e as Error).message || 'Save failed');
    } finally {
      setSavingAuthors(false);
    }
  };

  if (!wikiRead) {
    return (
      <div className="card-mdl-static p-6 text-sm text-slate-400">
        Portfolio wiki is disabled for your organisation.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-12 text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        <span>Loading portfolio briefing…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-dark-border bg-dark-surface p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-lg font-semibold text-slate-100 inline-flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-vivid-red" aria-hidden />
            Portfolio briefing
          </h3>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            {readiness.embeddingConfigured ? (
              <span className="rounded bg-emerald-900/30 px-2 py-0.5 text-emerald-200">
                Embeddings on
              </span>
            ) : (
              <span className="rounded bg-amber-900/30 px-2 py-0.5 text-amber-100">
                Embeddings off
              </span>
            )}
            <span>
              Indexed repos: {readiness.indexedRepositoryCount ?? 0} · Ready knowledge:{' '}
              {readiness.readyKnowledgeSourceCount ?? 0}
            </span>
            {wiki?.generatedAt ? (
              <span>
                Generated {formatBriefingDate(wiki.generatedAt)} using{' '}
                {wiki.geminiModel || 'Gemini'}
              </span>
            ) : null}
            {wiki?.generationStatus ? (
              <span className="font-mono text-slate-400">Status: {wiki.generationStatus}</span>
            ) : null}
            {readiness.wikiStale === true ? (
              <span
                className="rounded bg-amber-900/40 px-2 py-0.5 text-amber-100"
                title={buildStaleBriefingTitle(readiness) || 'Regenerate for fresher retrieval.'}
              >
                Briefing may be stale
              </span>
            ) : null}
          </div>
        </div>
        {wiki?.generationError ? (
          <p className="mb-3 rounded border-red-900 bg-red-950/50 p-2 text-red-200">
            {wiki.generationError}
          </p>
        ) : null}
        {!readiness.hasRetrievalSources ? (
          <p className="mb-3 text-sm text-slate-400">
            Connect repositories and wait for code indexing to succeed, or add knowledge sources
            below, before regenerating the briefing.
          </p>
        ) : null}
        {repositories?.length ? (
          <div className="mb-4">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">
              Repository code index
            </p>
            <ul className="divide-y divide-dark-border rounded-md border border-dark-border text-sm">
              {repositories.map((r) => {
                const inProgress = isCodeIndexInProgress(r);
                const pct =
                  typeof r.codeIndexProgress === 'number' && Number.isFinite(r.codeIndexProgress)
                    ? Math.max(0, Math.min(100, r.codeIndexProgress))
                    : 0;
                return (
                  <li
                    key={r.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                  >
                    <span className="min-w-0 truncate font-mono text-slate-200">
                      {r.fullName || r.name}
                    </span>
                    <span
                      className={`shrink-0 rounded px-2 py-0.5 text-xs ${codeIndexStatusBadgeClassForRepo(r)}`}
                    >
                      {inProgress
                        ? `${formatCodeIndexStageLabel(r.codeIndexStage)} ${pct}%`
                        : formatCodeIndexStatusLabel(r.codeIndexStatus, r.codeIndexStage)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="border-b border-dark-border mb-6">
        <nav
          className="flex gap-1 -mb-px overflow-x-auto"
          role="tablist"
          aria-label="Portfolio sections"
          onKeyDown={onTabKeyDown}
        >
          {PORTFOLIO_TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`portfolio-tab-${id}`}
              aria-selected={activeTab === id}
              aria-controls={`portfolio-tabpanel-${id}`}
              tabIndex={activeTab === id ? 0 : -1}
              onClick={() => setActiveTab(id)}
              className={animatedTabTriggerClass(activeTab === id)}
            >
              <AnimatedTabIndicator
                layoutId="portfolio-wiki-tab-indicator"
                isActive={activeTab === id}
              />
              <span className="relative z-10">{label}</span>
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && (
        <div
          role="tabpanel"
          id="portfolio-tabpanel-overview"
          aria-labelledby="portfolio-tab-overview"
        >
          <div className="rounded-md border border-dark-border bg-dark-base/20 p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-slate-100">Intro</h4>
            </div>
            <div className="portfolio-briefing-prose prose-invert max-w-none">
              {wiki?.introMarkdown ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, [rehypeSanitize, PORTFOLIO_MARKDOWN_SANITIZE_SCHEMA]]}
                  components={markdownComponents as any}
                >
                  {wiki.introMarkdown}
                </ReactMarkdown>
              ) : (
                <p className="text-slate-400">No intro yet. Regenerate after indexing.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'tech' && (
        <div role="tabpanel" id="portfolio-tabpanel-tech" aria-labelledby="portfolio-tab-tech">
          <div className="rounded-md border border-dark-border bg-dark-base/20 p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-slate-100">Tech features</h4>
              {showTechToc ? (
                <nav className="text-xs text-slate-400" aria-label="Tech features sections">
                  <span className="mr-2 uppercase tracking-wide text-slate-500">Jump to</span>
                  {techHeadings.map((h) => {
                    const id = slugifyHeading(h);
                    return (
                      <a
                        key={id}
                        href={`#tech-${id}`}
                        className="mr-2 underline decoration-gray-300 hover:decoration-gray-600"
                      >
                        {h.replace(/_/g, ' ')}
                      </a>
                    );
                  })}
                </nav>
              ) : null}
            </div>
            <div className="portfolio-briefing-prose prose-invert max-w-none">
              {wiki?.techFeaturesMarkdown ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, [rehypeSanitize, PORTFOLIO_MARKDOWN_SANITIZE_SCHEMA]]}
                  components={markdownComponents as any}
                >
                  {wiki.techFeaturesMarkdown}
                </ReactMarkdown>
              ) : (
                <p className="text-slate-400">No tech section yet.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'team' && (
        <div role="tabpanel" id="portfolio-tabpanel-team" aria-labelledby="portfolio-tab-team">
          <PortfolioTeamPanel
            orgId={orgId}
            streamId={streamId}
            projectId={projectId}
            repositories={repositories}
          />
        </div>
      )}

      {activeTab === 'settings' && (
        <div
          role="tabpanel"
          id="portfolio-tabpanel-settings"
          aria-labelledby="portfolio-tab-settings"
          className="space-y-6"
        >
          {wikiWrite ? (
            <div className="card-mdl-static p-5">
              <h3 className="mb-3 text-base font-semibold text-slate-100">
                Editorial &amp; regeneration
              </h3>
              <label
                className="block text-sm font-medium text-slate-300"
                htmlFor="portfolio-editorial"
              >
                Editorial instructions
              </label>
              <p className="mt-0.5 text-xs text-slate-400">
                These notes are passed to the model every time you regenerate. Be specific about
                what to focus on, what to skip, and who the audience is.
              </p>
              <textarea
                id="portfolio-editorial"
                className="mt-1 w-full rounded-md border border-dark-border bg-dark-base p-2 text-sm text-slate-100"
                rows={5}
                value={editorial}
                onChange={(e) => setEditorial(e.target.value)}
                placeholder={
                  'e.g. "Focus on the job scheduling module and mobile app architecture. Mention the Traccar integration prominently. The audience is new engineers joining the team. Ignore the legacy payment module."'
                }
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-secondary-mdl inline-flex items-center gap-2 text-sm disabled:opacity-50"
                  onClick={handleSaveEditorial}
                  disabled={savingEditorial}
                >
                  {savingEditorial ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Save className="h-4 w-4" aria-hidden />
                  )}
                  Save notes
                </button>
                <button
                  type="button"
                  className="btn-primary-mdl inline-flex items-center gap-2 text-sm disabled:opacity-50"
                  onClick={handleRegenerate}
                  disabled={
                    regenerating ||
                    genBusy ||
                    !readiness.hasRetrievalSources ||
                    !readiness.embeddingConfigured
                  }
                >
                  {regenerating || genBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <RefreshCw className="h-4 w-4" aria-hidden />
                  )}
                  Regenerate briefing
                </button>
              </div>
              <p className="mt-2 text-xs text-slate-400">
                Uses Gemini ({wiki?.geminiModel || 'GEMINI_MODEL'}) with RAG over indexed code and
                processed knowledge for “{projectName}”.
              </p>
            </div>
          ) : null}

          {wikiWrite ? (
            <div className="card-mdl-static p-5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-slate-100">
                  Author roster (for prompts)
                </h3>
                <button type="button" className="btn-secondary-mdl text-sm" onClick={addAuthorRow}>
                  Add row
                </button>
              </div>
              <p className="mb-3 text-xs text-slate-400">
                Maps organisation people to PM / Analyst / Engineering / QA. The model uses this
                list when writing the Authors section.
              </p>
              {assignments.length === 0 ? (
                <p className="mb-3 text-sm text-slate-400">
                  No rows yet — add one per lead or discipline.
                </p>
              ) : (
                <ul className="space-y-2">
                  {assignments.map((row, index) => (
                    <li
                      key={row.id || `new-${index}`}
                      className="flex flex-col gap-2 rounded border border-dark-border p-2 sm:flex-row sm:items-center"
                    >
                      <select
                        className="rounded-md border border-dark-border bg-dark-base px-2 py-1 text-sm"
                        value={row.role}
                        onChange={(e) => updateAuthorRow(index, { role: e.target.value })}
                      >
                        {AUTHOR_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <select
                        className="min-w-0 flex-1 rounded-md border border-dark-border bg-dark-base px-2 py-1 text-sm"
                        value={row.userId || ''}
                        onChange={(e) => updateAuthorRow(index, { userId: e.target.value || null })}
                      >
                        <option value="">— Member —</option>
                        {orgMembers.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.username || m.email || m.id}
                          </option>
                        ))}
                      </select>
                      <input
                        className="min-w-0 flex-1 rounded-md border border-dark-border bg-dark-base px-2 py-1 text-sm"
                        placeholder="Display name (if no member)"
                        value={row.displayName || ''}
                        onChange={(e) => updateAuthorRow(index, { displayName: e.target.value })}
                      />
                      <button
                        type="button"
                        className="btn-icon-mdl shrink-0"
                        onClick={() => removeAuthorRow(index)}
                        aria-label="Remove row"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                className="btn-primary-mdl mt-3 inline-flex items-center gap-2 text-sm disabled:opacity-50"
                onClick={handleSaveAuthors}
                disabled={savingAuthors}
              >
                {savingAuthors ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Save className="h-4 w-4" aria-hidden />
                )}
                Save author roster
              </button>
            </div>
          ) : null}

          {knowRead ? (
            <ProjectDocumentsPanel
              orgId={orgId}
              streamId={streamId}
              projectId={projectId}
              knowRead={knowRead}
              knowWrite={knowWrite}
              title="Knowledge sources"
              description="Add roadmaps, ADRs, Confluence exports, security reviews, or other prose so the briefing is not only code-shaped. Use Browse Google Drive or paste URLs; manage all project documents from the Documents tab."
            />
          ) : null}

          {wikiDebugRead && wiki?.retrievalDebug ? (
            <div className="card-mdl-static p-5">
              <button
                type="button"
                className="flex w-full items-center justify-between text-left text-sm font-semibold text-slate-100"
                onClick={() => setRetrievalDebugOpen((o) => !o)}
                aria-expanded={retrievalDebugOpen}
              >
                <span>Retrieval debug</span>
                <span className="text-slate-400">{retrievalDebugOpen ? '−' : '+'}</span>
              </button>
              {retrievalDebugOpen ? (
                <pre className="mt-2 max-h-64 overflow-auto rounded bg-dark-base p-2 text-xs text-slate-200">
                  {JSON.stringify(wiki.retrievalDebug, null, 2)}
                </pre>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
